import * as L from 'leaflet';
import { VOYAGE_POI_RADIUS_KM } from 'src/app/services/operators/voyageradar-credit-actions';
import {
  buildVoyagePoiNearbyPopup,
  buildVoyagePoiNearbyRows,
  VoyagePoiPopupContext,
} from '../poi-nearby-popup';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
// The provider's payload dialect — imported since phase 9, see search.feature.
import { vrToLocatedRecord } from '../gateways/vr-lookup.gateway';

/**
 * What this feature must be TOLD rather than know (phase-6 boundary). Each
 * callback is a seam to code still living on the facade — the located/lookup
 * cluster that phase 7 finishes:
 */
export interface PoiNearbyFeatureOptions {
  /** Price of the by-MMSI lookup a row click spends — the card quotes it. */
  lookupCredits: () => number;
  /** Draw a kept vessel at its stored fix and select it — free. */
  locateHistoryRecord: (rec: any) => void;
  /** Make an already-drawn located vessel the selection. */
  selectLocated: (mmsi: string, opts: { openPopup?: boolean; fly?: boolean }) => void;
  /** The paid by-MMSI lookup; returns whether a request actually started. */
  refreshVesselLookup: (
    mmsi: string,
    opts: { draw?: boolean; select?: boolean; onSettled?: () => void },
  ) => boolean;
}

/**
 * POI NEARBY — the tasked-point task's result list, as a pluggable feature
 * (phase 6, REFACTOR-PLAN.md §7; PROVIDER-OWNED per decision D7).
 *
 * A POI task's refresh returns a positionless list of what is around the
 * tasked point, so nothing is drawn from it: the list hangs off the reticle as
 * a popup, and a row click BUYS that vessel's fix through the by-MMSI lookup,
 * placing it exactly like a search result.
 *
 * It fills the tasked target but never owns it — the reticle and disc belong
 * to the sensor checkbox (`TargetController`), and building a second pair here
 * is what used to leave a POI target blinking out on every snapshot swap. The
 * one thing this feature does own is the reticle's LOOK: `buildPoiReticle` is
 * handed to the target controller as its `buildReticle` callback, because the
 * marker exists to carry this popup.
 */
export class PoiNearbyFeature extends AisMapFeature {
  /**
   * Rows shown in the POI popup, in the operator's nearest-first order.
   *
   * The list is the POI refresh's ENTIRE result: /v1/vessels/nearby returns no
   * positions, so nothing is drawn from it — a row click buys that vessel's
   * full details (fix included) via the by-MMSI lookup and places it exactly
   * like a search result.
   */
  private poiVessels: any[] = [];
  /**
   * What the filter box currently holds, and which row's lookup is in flight.
   *
   * Both live here rather than in the DOM because the rows container is
   * re-rendered from `poiVessels` by two separate triggers — a keystroke and a
   * row click — and each has to preserve what the other set.
   */
  private poiQuery = '';
  private poiPendingMmsi: string | null = null;

  constructor(ctx: AisMapContext, private readonly opts: PoiNearbyFeatureOptions) {
    super(ctx);
  }

  /**
   * A POI refresh landed (or a stored one was recalled): fill the tasked target
   * with the result, keep its disc at the fixed 1,000 km POI radius, and open
   * the nearby-vessel list as the reticle's popup.
   *
   * It no longer BUILDS the reticle and disc. Those are the target, the
   * checkbox owns them, and by the time a refresh lands they are already on the
   * map; building a second pair here is what used to leave a POI task's target
   * blinking out on every snapshot swap and gone for good on a clear.
   */
  showPoiNearby(scene: any, vessels: any[]): void {
    const [lng, lat] = scene?.footprint?.coordinates ?? [];
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || !this.ctx.host.map) return;

    // The reticle and disc are the TARGET, which the checkbox owns and which is
    // very likely already up — this pull only fills them with a result. Building
    // them here (as this used to) put a second reticle over the first and tied
    // the target's life to the data's.
    this.poiVessels = Array.isArray(vessels) ? vessels : [];
    this.ctx.target.radiusKm = VOYAGE_POI_RADIUS_KM;
    // Passing the scene this refresh belongs to, rather than letting the task
    // be re-resolved: this one is already known to be the right geometry.
    if (!this.ctx.target.showTarget({ scene })) return;
    // Keep the visible POI coverage disc at the fixed platform radius.
    this.ctx.target.circle?.setRadius?.(this.ctx.target.radiusKm * 1000);

    const marker = this.ctx.target.marker;
    if (!marker) return;
    this.bindPoiPopup(marker);
    this.ctx.popup.ensurePopupActionsWired();

    // Frame the disc, then present the list — the popup IS the result.
    this.ctx.target.fitTarget();
    marker.openPopup();
  }

  /**
   * The amber reticle at a POI task's tasked point: ring + centre dot, styled
   * inline because divIcon HTML renders outside every Angular component. A
   * custom className REPLACES Leaflet's 'leaflet-div-icon' default, so the
   * white box never applies and no stylesheet entry is needed — the class is
   * purely an identifier.
   *
   * Carries no popup: the reticle goes up with the checkbox, before any pull,
   * and a card offering an empty list would be worse than no card. The popup is
   * bound by `showPoiNearby` once there is a list to show.
   *
   * PUBLIC because the target controller builds the marker through it (its
   * `buildReticle` option): the reticle belongs to the target, but its look —
   * and the popup it carries — belong to this feature.
   */
  buildPoiReticle(lat: number, lng: number): any {
    const icon = L.divIcon({
      className: 'vr-poi-target',
      html:
        '<span style="display:block;width:22px;height:22px;border-radius:50%;' +
        'border:2px solid #fbbf24;box-shadow:0 0 0 2px rgba(15,23,42,.55);position:relative;">' +
        '<span style="position:absolute;left:50%;top:50%;width:6px;height:6px;' +
        'margin:-3px 0 0 -3px;border-radius:50%;background:#fbbf24;"></span></span>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -12],
    });
    const marker = L.marker([lat, lng], { icon, zIndexOffset: 400 });
    // Wired on the MARKER, not per bind: the filter box is plain DOM inside the
    // popup, so it has to be re-wired on every open (each open re-renders the
    // card from the content function), and `clearPoiNearby` unbinds the popup
    // without touching the marker — re-registering there would stack a second
    // listener on every pull.
    marker.on('popupopen', (e: any) => this.wirePoiFilter(e?.popup));
    return marker;
  }

  /**
   * Hangs the nearby-vessel list off the reticle, once.
   *
   * The content is a FUNCTION, so every open re-renders against current state —
   * a vessel placed on the map shows its "On map" badge the next time the list
   * opens, without any re-render plumbing. That is also why re-binding on a
   * second pull is unnecessary: the same closure reads whatever `poiVessels`
   * holds now.
   */
  private bindPoiPopup(marker: any): void {
    if (marker.getPopup?.()) return;
    marker.bindPopup(
      () => buildVoyagePoiNearbyPopup(this.poiVessels, this.poiPopupContext()),
      {
        className: 'ais-vessel-popup',
        closeButton: false,
        // Wider than a vessel card: this one carries two lines per row and a
        // right-aligned distance column, and the card's 300px was where long
        // names started eating the sub-line.
        maxWidth: 340,
        minWidth: 260,
        offset: L.point(0, -10),
        autoPanPadding: L.point(40, 90),
      },
    );
  }

  /** Everything the nearby list renders against, resolved fresh on every render. */
  private poiPopupContext(): VoyagePoiPopupContext {
    return {
      lookupCredits: this.opts.lookupCredits(),
      onMap: (m) => this.ctx.located.isLocatedOnMap(m),
      radiusKm: this.ctx.target.radiusKm,
      pendingMmsi: this.poiPendingMmsi,
    };
  }

  /**
   * Re-renders the nearby rows in place — the ONLY thing that redraws them.
   *
   * The rows container alone, never the card: the filter input lives outside it
   * and keeps its focus and caret, which rebuilding the popup would drop after
   * the first letter. Rows come from `poiVessels`, the complete list, so a match
   * beyond the rendered cap is still reachable.
   *
   * Both triggers — a keystroke and a lookup starting or ending — go through
   * here, so neither can wipe what the other set. `keepScroll` is the
   * difference between them: a filter is a NEW list and belongs at the top,
   * while a row going busy must not yank the list out from under the operator.
   */
  private renderPoiRows(opts: { keepScroll?: boolean } = {}): void {
    const el = this.ctx.target.marker?.getPopup?.()?.getElement?.();
    const rows: HTMLElement | null = el?.querySelector?.('.ais-popup__poirows');
    if (!rows) return;
    const scroll = rows.scrollTop;
    rows.innerHTML = buildVoyagePoiNearbyRows(
      this.poiVessels,
      this.poiPopupContext(),
      this.poiQuery,
    );
    rows.scrollTop = opts.keepScroll ? scroll : 0;
  }

  /**
   * Marks one row as having its lookup in flight, or clears the mark.
   *
   * The card root carries it too, which is what stands the other rows down:
   * only one lookup runs at a time, so while this one is out every other row is
   * an offer the guard in `refreshVesselLookup` would silently refuse.
   */
  private setPoiBusy(mmsi: string | null): void {
    this.poiPendingMmsi = mmsi;
    const card = this.ctx.target.marker?.getPopup?.()?.getElement?.()
      ?.querySelector?.('.ais-popup--poi');
    card?.classList?.toggle('is-busy', !!mmsi);
    this.renderPoiRows({ keepScroll: true });
  }

  /**
   * Wires the filter box on every popup open (each open re-renders the card
   * from the content function, so the old node and its listeners are gone).
   *
   * Keystrokes are stopped from reaching the map: Leaflet's keyboard handler
   * pans on arrows and zooms on +/-, and it binds while the map has focus.
   */
  private wirePoiFilter(popup: any): void {
    // The card was just rebuilt unfiltered, so the remembered query is stale —
    // clearing it first keeps this state and the DOM in step even on the short
    // lists that never get a filter box at all.
    this.poiQuery = '';

    const el = popup?.getElement?.();
    const input: HTMLInputElement | null = el?.querySelector?.('.ais-popup__poifilter');
    if (!input) return;

    L.DomEvent.on(input, 'keydown keypress keyup', L.DomEvent.stopPropagation);
    L.DomEvent.on(input, 'input', () => {
      this.poiQuery = input.value;
      this.renderPoiRows();
    });
  }

  /**
   * Takes the nearby RESULT off the map — the popup and the list behind it.
   *
   * The reticle and disc stay: they are the tasked target, they belong to the
   * checkbox, and this runs on every snapshot swap. Removing them here is what
   * used to make a POI task's target blink out whenever the panel changed what
   * was shown, and vanish for good on a clear.
   *
   * The disc goes back to the TASKED radius, since the served radius it was
   * carrying described a result that is no longer on the map.
   */
  clearPoiNearby(): void {
    this.ctx.target.marker?.closePopup?.();
    this.ctx.target.marker?.unbindPopup?.();
    this.poiVessels = [];
    this.ctx.target.radiusKm = null;
    this.poiQuery = '';
    this.poiPendingMmsi = null;
    this.ctx.target.circle?.setRadius?.(VOYAGE_POI_RADIUS_KM * 1000);
  }

  /**
   * A row of the nearby list was clicked. Free when the hull is already
   * drawn or kept with a stored fix (same rule as the panel's Locate); the
   * paid by-MMSI lookup otherwise — which is the ONLY way to obtain a
   * position for a nearby row, since the list itself carries none.
   *
   * PUBLIC: the shared popup host's click delegate dispatches here (the card
   * is plain DOM, so the delegate is the only route back into the app).
   */
  pickPoiNearbyVessel(mmsi: string): void {
    const m = String(mmsi ?? '').trim();
    if (!m) return;
    if (this.ctx.located.isLocatedOnMap(m)) {
      this.opts.selectLocated(m, { openPopup: true, fly: true });
      return;
    }
    const rec = (this.ctx.sensor?.vesselHistory ?? []).find(
      (v: any) => String(v?.mmsi) === m,
    );
    const stored = rec ? vrToLocatedRecord(rec) : null;
    if (stored && stored.lat != null && stored.lng != null) {
      this.opts.locateHistoryRecord(rec);
      return;
    }
    // Paid path: latest fix + full details, placed like a search result. The
    // wallet gate, spend mirror and failure copy all live in the lookup.
    //
    // The row is marked busy only if a request actually STARTED — the lookup
    // turns several clicks back at the door (no wallet, one already in flight),
    // and a row left spinning for a request that was never made is a worse lie
    // than no feedback at all. `onSettled` fires on both the success and the
    // failure path, including the ones that leave this popup open.
    const started = this.opts.refreshVesselLookup(m, {
      draw: true,
      select: true,
      onSettled: () => this.setPoiBusy(null),
    });
    if (started) this.setPoiBusy(m);
  }

  /**
   * Drops the POI result on a task switch — FIELDS ONLY (invariant I1). The
   * reticle and disc are the target's, and the shared sweep has already taken
   * them off the map; `TargetController.dropRefs()` nulls those.
   */
  override reset(): void {
    this.poiVessels = [];
    this.poiQuery = '';
    this.poiPendingMmsi = null;
  }
}
